"use client";

import { type ReactNode, useId } from "react";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { DisplaySettingsRow } from "./display-settings";

type DisplaySettingsSelectOption<TValue extends string> = {
  value: TValue;
  label: ReactNode;
  disabled?: boolean;
};

type DisplaySettingsSelectRowProps<TValue extends string> = {
  label: ReactNode;
  value: TValue;
  options: readonly DisplaySettingsSelectOption<TValue>[];
  onValueChange: (value: TValue) => void;
  disabled?: boolean;
  disabledReason?: ReactNode;
  placeholder?: string;
};

/**
 * label と controlled Select を標準行へ配置します。
 * disabled 時は変更を通知せず、理由があれば control の accessible description にします。
 */
function DisplaySettingsSelectRow<TValue extends string>({
  label,
  value,
  options,
  onValueChange,
  disabled = false,
  disabledReason,
  placeholder,
}: DisplaySettingsSelectRowProps<TValue>) {
  const labelId = useId();
  const reasonId = useId();

  return (
    <DisplaySettingsRow className="grid grid-cols-[minmax(0,1fr)_9rem] items-center gap-3">
      <div className="min-w-0">
        <span
          id={labelId}
          className="block text-sm font-normal leading-5 text-muted-foreground"
        >
          {label}
        </span>
        {disabled && disabledReason ? (
          <span
            id={reasonId}
            className="mt-0.5 block text-xs leading-4 text-muted-foreground"
          >
            {disabledReason}
          </span>
        ) : null}
      </div>
      <Select
        value={value}
        onValueChange={(nextValue) => onValueChange(nextValue as TValue)}
        disabled={disabled}
      >
        <SelectTrigger
          aria-labelledby={labelId}
          aria-describedby={disabled && disabledReason ? reasonId : undefined}
          className="h-8 w-full min-w-0 rounded-md bg-transparent px-2 text-sm font-normal shadow-xs [&>span]:truncate"
        >
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent data-display-settings-select-content="">
          <SelectGroup>
            {options.map((option) => (
              <SelectItem
                key={option.value}
                value={option.value}
                disabled={option.disabled}
              >
                {option.label}
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </Select>
    </DisplaySettingsRow>
  );
}

type DisplaySettingsSegmentOption<TValue extends string> = {
  value: TValue;
  label: ReactNode;
};

type DisplaySettingsSegmentRowProps<TValue extends string> = {
  label: ReactNode;
  value: TValue;
  options: readonly DisplaySettingsSegmentOption<TValue>[];
  onValueChange: (value: TValue) => void;
};

/**
 * 排他的な controlled segment を標準行へ配置します。
 * Radix が発行する空選択は無視し、常に現在値を1件維持します。
 */
function DisplaySettingsSegmentRow<TValue extends string>({
  label,
  value,
  options,
  onValueChange,
}: DisplaySettingsSegmentRowProps<TValue>) {
  const labelId = useId();

  return (
    <DisplaySettingsRow className="grid grid-cols-[minmax(0,1fr)_9rem] items-center gap-3">
      <span
        id={labelId}
        className="block text-sm font-normal leading-5 text-muted-foreground"
      >
        {label}
      </span>
      <ToggleGroup
        type="single"
        variant="outline"
        size="sm"
        value={value}
        aria-labelledby={labelId}
        className="grid h-8 grid-cols-2 gap-1"
        onValueChange={(nextValue) => {
          if (nextValue) onValueChange(nextValue as TValue);
        }}
      >
        {options.map((option) => (
          <ToggleGroupItem
            key={option.value}
            value={option.value}
            className="h-8 min-w-0 px-2 text-xs font-normal"
          >
            {option.label}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
    </DisplaySettingsRow>
  );
}

type DisplaySettingsSwitchRowProps = {
  label: ReactNode;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
};

/** label クリックにも対応する controlled Switch を標準行へ配置します。 */
function DisplaySettingsSwitchRow({
  label,
  checked,
  onCheckedChange,
}: DisplaySettingsSwitchRowProps) {
  const switchId = useId();

  return (
    <DisplaySettingsRow className="flex items-center justify-between gap-4">
      <label
        htmlFor={switchId}
        className="min-w-0 cursor-pointer text-sm font-normal leading-5 text-muted-foreground"
      >
        {label}
      </label>
      <Switch
        id={switchId}
        checked={checked}
        onCheckedChange={onCheckedChange}
      />
    </DisplaySettingsRow>
  );
}

export {
  DisplaySettingsSegmentRow,
  DisplaySettingsSelectRow,
  DisplaySettingsSwitchRow,
};
